import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Landmark, Loader2, ShieldCheck, TrendingUp, FileCheck2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 brand-gradient text-white flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, white 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="relative flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-emerald-400/20 border border-emerald-300/30 flex items-center justify-center">
            <Landmark className="h-6 w-6 text-emerald-300" />
          </div>
          <div>
            <p className="font-bold text-lg" style={{ fontFamily: "Outfit" }}>EXTRA TRUSS FINANCE</p>
            <p className="text-xs text-emerald-200/80 font-mono tracking-widest">CASH & EXPENSE PORTAL</p>
          </div>
        </div>
        <div className="relative space-y-8 max-w-md">
          <h2 className="text-4xl font-bold leading-tight" style={{ fontFamily: "Outfit" }}>
            Kelola pengajuan kas & pertanggungjawaban dalam satu portal.
          </h2>
          <div className="space-y-4">
            {[
              { icon: FileCheck2, t: "Approval Berjenjang", d: "Direktur & Approver menyetujui dengan catatan." },
              { icon: TrendingUp, t: "Saldo Terpantau", d: "Sisa kas belum dipertanggungjawabkan real-time." },
              { icon: ShieldCheck, t: "Bukti Wajib", d: "Upload bukti hingga saldo lunas." },
            ].map((f, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                  <f.icon className="h-5 w-5 text-emerald-300" />
                </div>
                <div>
                  <p className="font-semibold">{f.t}</p>
                  <p className="text-sm text-emerald-100/70">{f.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-emerald-200/60 font-mono">© 2026 EXTRA TRUSS FINANCE</p>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-11 w-11 rounded-xl brand-gradient flex items-center justify-center">
              <Landmark className="h-5 w-5 text-emerald-300" />
            </div>
            <p className="font-bold text-brand" style={{ fontFamily: "Outfit" }}>EXTRA TRUSS FINANCE</p>
          </div>
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: "Outfit" }}>Masuk ke Portal</h1>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Gunakan email dan password yang didaftarkan IT Admin.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="login-email-input" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="nama@trussfinance.com" required className="mt-1.5" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-brand hover:underline">Lupa password?</Link>
              </div>
              <Input id="password" type="password" data-testid="login-password-input" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="mt-1.5" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}
            <Button type="submit" data-testid="login-submit-btn" disabled={loading}
              className="w-full bg-brand hover:bg-emerald-800 h-11 text-base">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Masuk"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
